import { Avatar, Divider, message, Popover, Tooltip } from 'antd';
import {
  SupplementalKnowledgeValue,
  updateSupplementKnowledge,
} from 'services/api/supplementKnowledge';
import { getTranslateContent } from '../utils';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { eventBus } from 'services/eventBus';
import { useTranslate } from '@tolgee/react';
import { useLayoutEffect, useState } from 'react';
import { Section } from './Section';
import { KnowledgeSelector } from 'components/Dialogue/components/QAPair/KnowledgeSelector';
import { getQAQueries } from 'hooks/business/im/useQAPairTrigger';
import { useQuery } from '@tanstack/react-query';
import { queries } from 'services/queries';
import styled from '@emotion/styled';
import Icon, { DownOutlined, LoadingOutlined } from '@ant-design/icons';
import { ReactComponent as TranslateSvg } from 'views/Conversation/assets/translate.svg';
import { ReactComponent as GoogleSvg } from 'views/Conversation/assets/google.svg';
import { ReactComponent as GptSvg } from 'views/Conversation/assets/gpt.svg';
import { ReactComponent as DeepseekSvg } from 'views/Conversation/assets/deepseek.svg';
import { ReactComponent as ChatPlusAISvg } from 'views/Conversation/assets/chatplusai.svg';
import { css } from '@emotion/css';
import { useRequest } from 'ahooks';
import { modeTranslate, TranslateMode } from 'services/api/overseaTranslate';

export interface PureBuyerQuestionProps {
  extra?: React.ReactNode;
  buyerNick: string;
  question: string;
  questionTranslate?: string;
  onLocate?: () => void;
  source?: SupplementKnowledge.Source;
}

export const PureBuyerQuestionContent = (props: PureBuyerQuestionProps) => {
  const { question, questionTranslate, buyerNick, onLocate, source } = props;
  const systemLanguage = useSystemLanguageValue();
  const { t } = useTranslate();

  const { data } = useQuery({
    enabled: !!question && !questionTranslate,
    ...queries.translates.overseaTranslate(question, systemLanguage, 'supplementKnowledge'),
  });

  const initTranslateContent = questionTranslate || data;

  const {
    loading,
    data: translated,
    run,
  } = useRequest(
    (mode: TranslateMode) => {
      return modeTranslate(question, systemLanguage, mode, {
        isUseRedisCache: !initTranslateContent,
      });
    },
    {
      manual: true,
      onError: () => message.error(t('marketing.翻译失败，请稍后再试')),
    },
  );

  const translateContent = translated || initTranslateContent;

  const content = (
    <div
      style={onLocate ? { cursor: 'pointer' } : undefined}
      className="bg-[var(--fett-knowledge-question-bubble-bg)] px-[10px] py-2 rounded-sm flex-1"
      onClick={onLocate}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs text-[var(--fett-text-secondary)]">{question}</span>
      </div>
      {translateContent && (
        <>
          <Divider className="!my-2" />
          <span className="text-xs text-[var(--fett-text-tertiary)]">
            {loading ? <LoadingOutlined /> : translateContent}
          </span>
        </>
      )}
    </div>
  );

  const [visible, setVisible] = useState(false);

  const translate = (
    <Popover
      visible={visible}
      onVisibleChange={setVisible}
      placement="bottom"
      align={{ offset: [0, -4] }}
      overlayClassName={styles.popover}
      content={(
        [
          { title: 'Google', mode: 'GOOGLE', icon: GoogleSvg },
          { title: 'ChatGPT', mode: 'CHAT_GPT', icon: GptSvg },
          { title: 'DeepSeek', mode: 'DEEPSEEK', icon: DeepseekSvg },
          { title: 'ChatPlusAI', mode: 'CHATPLUSAI', icon: ChatPlusAISvg },
        ] as const
      ).map(({ title, mode, icon }) => {
        return (
          <Tooltip
            key={mode}
            placement="topRight"
            getTooltipContainer={(_) => _}
            title={t('ViewsChat.使用x重新翻译', { x: title })}
          >
            <div
              onClick={(e) => {
                e.stopPropagation();

                run(mode);

                setVisible(false);
              }}
            >
              <Icon component={icon} />
            </div>
          </Tooltip>
        );
      })}
    >
      <Translate title={t('phrase.翻译')}>
        <Icon component={TranslateSvg} />
        <DownOutlined />
      </Translate>
    </Popover>
  );

  return (
    <>
      <div className="flex gap-2">
        <Avatar className="!bg-[#E9386F]" shape="square" size={32}>
          {buyerNick?.slice(0, 1).toUpperCase()}
        </Avatar>
        {!!onLocate ? (
          <Tooltip title={t('定位至买家问题')} placement="bottom">
            {content}
          </Tooltip>
        ) : (
          content
        )}
        {translate}
      </div>
      {source === 'MERCADO_QA' && (
        <div
          style={{
            display: 'inline-block',
            marginLeft: 40,
            marginTop: 4,
            lineHeight: '24px',
            padding: '0 12px',
            fontSize: 12,
            color: '#515359',
            background: '#F0F2F5',
            borderRadius: 2,
          }}
        >
          {t('来源')}: Mercado Q&A
        </div>
      )}
    </>
  );
};

export const PureBuyerQuestion = (props: PureBuyerQuestionProps) => {
  const { extra } = props;
  const { t } = useTranslate();

  return (
    <Section title={t('买家问题小结')} extra={extra}>
      <PureBuyerQuestionContent {...props} />
    </Section>
  );
};

interface BuyerQuestionProps {
  citeMode?: boolean;
  conversation?: Conversation.Conversation;
  knowledgeId?: number;
  supplementalKnowledgeValue: SupplementalKnowledgeValue;
  showKnowledgeSelector?: boolean;
  onRefresh?: () => void;
  autoLocate?: boolean;
}

export const BuyerQuestion = (props: BuyerQuestionProps) => {
  const {
    citeMode,
    conversation,
    knowledgeId,
    supplementalKnowledgeValue,
    showKnowledgeSelector,
    autoLocate = true,
    onRefresh,
  } = props;
  const { buyerNick, rewriteQuestion, rewriteQuestionTranslateMap, question } =
    supplementalKnowledgeValue;
  const { topBarInfo } = conversation || {};

  const language = useSystemLanguageValue();
  const qTranslate = getTranslateContent(
    language,
    rewriteQuestionTranslateMap || question?.translateMap,
  );

  const locate = citeMode
    ? undefined
    : () => {
        if (!question?.messageId) return;
        eventBus.emit('SCROLL_TO_MSG_BY_ID', question.messageId);
      };

  const { validQueries } = getQAQueries(topBarInfo?.knowledgeReplies);

  const handleQuestionChange = async (knowledgeQuery: Conversation.Message) => {
    if (!knowledgeId) return;
    const { msgId, textContent } = knowledgeQuery;
    await updateSupplementKnowledge({
      id: knowledgeId,
      supplementalKnowledgeValue: {
        question: {
          messageId: msgId,
          message: textContent?.text || '',
        },
        rewriteQuestion: textContent?.text || '',
      },
    });
    onRefresh?.();
  };

  useLayoutEffect(() => {
    if (!autoLocate) return;

    const timer = setTimeout(() => {
      locate?.();
    }, 1000);
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLocate]);

  return (
    <PureBuyerQuestion
      buyerNick={buyerNick}
      question={rewriteQuestion || question?.message || ''}
      questionTranslate={qTranslate}
      extra={
        showKnowledgeSelector && validQueries.length > 1 ? (
          <KnowledgeSelector
            value={`${question?.messageId}`}
            knowledgeSelections={validQueries}
            onChange={handleQuestionChange}
          />
        ) : null
      }
      onLocate={locate}
    />
  );
};

const Translate = styled.span`
  align-self: flex-end;
  display: flex;
  align-items: center;
  gap: 0 2px;
  height: 24px;
  padding: 0 4px;
  color: var(--fett-text-tertiary);
  border: solid 1px var(--fett-border-strong);
  border-radius: 2px;
  cursor: pointer;
  .anticon {
    font-size: 16px;
  }
  .anticon-down {
    font-size: 10px;
  }
`;

const styles = {
  popover: css`
    .ant-popover-arrow {
      display: none;
    }
    .ant-popover-inner-content {
      display: flex;
      align-items: center;
      padding: 4px;
      > div {
        width: 24px;
        height: 24px;
        line-height: 24px;
        text-align: center;
        font-size: 16px;
        cursor: pointer;
        &:hover {
          background: var(--fett-bg-app);
        }
      }
    }
  `,
};
